import type { FieldPath, FieldValues } from 'react-hook-form';
import { Box } from '@mui/material';
import RhfSelect, {
  type RhfSelectProps,
} from '@core/components/forms/fields/RhfSelect';
import type { MaterialColor } from '../../../api/materialColor';
import { ColorSwatch } from '../../components/ColorSwatch';
import MaterialColorDialog from '../../components/MaterialColorDialog';
import { useAppContext } from '../../context/AppContext';
import { MATERIAL_KIND_LABELS, MaterialKind } from '../../enum/materialKind';
import { useCreateActionOption } from '../../hooks/useCreateActionOption';
import { formatMaterialLabel } from '../../utils/orderItem';

type RhfMaterialSelectProps<T extends FieldValues> = {
  name: FieldPath<T>;
  label: string;
  kind: MaterialKind;
} & Omit<
  RhfSelectProps<T, MaterialColor, MaterialColor>,
  | 'name'
  | 'label'
  | 'items'
  | 'getOptionValue'
  | 'getOptionLabel'
  | 'renderOption'
  | 'renderSelectionAdornment'
  | 'groupBy'
  | 'options'
  | 'actionOption'
>;

export function RhfMaterialSelect<T extends FieldValues>({
  name,
  label,
  kind,
  ...rest
}: RhfMaterialSelectProps<T>) {
  const { threads, trimmings, galoons, cords, tassels } = useAppContext();
  const items = {
    [MaterialKind.Thread]: threads,
    [MaterialKind.Trimming]: trimmings,
    [MaterialKind.Galoon]: galoons,
    [MaterialKind.Cord]: cords,
    [MaterialKind.Tassel]: tassels,
  }[kind];
  const materialAction = useCreateActionOption<MaterialColor>(
    `Add new ${MATERIAL_KIND_LABELS[kind].singular.toLowerCase()}…`,
    (props) => <MaterialColorDialog {...props} kind={kind} />
  );

  return (
    <RhfSelect<T, MaterialColor, MaterialColor>
      name={name}
      label={label}
      items={items}
      getOptionValue={(material) => String(material.id)}
      getOptionLabel={formatMaterialLabel}
      groupBy={(material) => material.typeName ?? ''}
      renderOption={(option) => (
        <ColorSwatch hexCode={option.item.hexCode}>
          {option.item.name}
        </ColorSwatch>
      )}
      renderSelectionAdornment={(option) => (
        <Box sx={{ ml: 0.5 }}>
          <ColorSwatch hexCode={option.item.hexCode} />
        </Box>
      )}
      actionOption={materialAction}
      {...rest}
    />
  );
}
